import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft, faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { formatFileSize } from '../../../../components/FormField';
import { type FileMeta, formatToExt, originalExt } from './imageCompression';

interface FileRowProps {
  file: File;
  meta: FileMeta;
  onUpdate: (patch: Partial<FileMeta>) => void;
  onRemove: () => void;
  onOpenModal: () => void;
}

export default function FileRow({ file, meta, onUpdate, onRemove, onOpenModal }: FileRowProps) {
  const isImage = file.type.startsWith('image/');
  const ext = meta.compress && isImage ? formatToExt(meta.format) : originalExt(file.name);

  return (
    <div style={{ backgroundColor: 'var(--color-base-300)', padding: '4px' }}>
      <div className="flex items-center gap-2 p-2 bg-base-200">

        {isImage ? (
          <img
            src={URL.createObjectURL(file)}
            alt={file.name}
            className="w-12 h-12 object-cover flex-shrink-0"
            onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)}
          />
        ) : (
          <div className="w-12 h-12 bg-base-300 flex items-center justify-center flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
        )}

        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={meta.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              className="input input-xs input-bordered flex-1 min-w-0"
              style={{ backgroundColor: 'var(--color-base-200)' }}
            />
            <span className="text-xs text-base-content/60 flex-shrink-0">{ext}</span>
          </div>
          <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
            <span className="text-xs text-base-content/70 flex-shrink-0">
              {meta.compress && meta.compressedSize !== null ? (
                <span className="text-success font-medium">
                  {formatFileSize(meta.compressedSize)}
                  <span className="text-success/70 ml-1">
                    (-{Math.round((1 - meta.compressedSize / file.size) * 100)}%)
                  </span>
                </span>
              ) : formatFileSize(file.size)}
            </span>
            {isImage && (
              <label className="flex items-center gap-1 cursor-pointer text-xs flex-shrink-0">
                <input
                  type="checkbox"
                  className="checkbox checkbox-xs"
                  checked={meta.compress}
                  onChange={(e) => onUpdate({ compress: e.target.checked })}
                />
                Compress
              </label>
            )}
            {isImage && meta.compress && (
              <button
                type="button"
                className="btn btn-xs btn-ghost flex-shrink-0"
                style={{ padding: '0 4px', minHeight: 'unset', height: 20, gap: 3 }}
                onClick={onOpenModal}
              >
                <FontAwesomeIcon icon={faSlidersH} />
                {meta.format.toUpperCase()}·{meta.quality}%
              </button>
            )}
          </div>
        </div>

        <div className="tooltip tooltip-left flex-shrink-0" data-tip="Remove">
          <button type="button" onClick={onRemove} className="btn btn-sm btn-ghost btn-circle">
            <FontAwesomeIcon icon={faDeleteLeft} />
          </button>
        </div>

      </div>
    </div>
  );
}
